import Phaser from 'phaser';
import type { ProcessedTextures } from '../../rendering/TextureManager';
import type { TokiPonaAudio } from '../../toki/TokiPonaAudio';
import { atlasCreateImage } from '../../rendering/AtlasUtils';

// Phase timings (ms from phase start)
const DEATH_FREEZE_MS = 300;       // brief freeze
const DEATH_FLASH_MS = 480;        // 3 flashes: 80ms on + 80ms off x3
const DEATH_REDSKY_MS = 600;       // red sky + black blocks
const DEATH_BLACKSKY_MS = 1200;    // black sky + skull falls

/**
 * Callbacks that DeathAnimationSystem uses to mutate GameScene visuals.
 * Keeps the animation system decoupled from GameScene's internals.
 */
export interface DeathAnimationCallbacks {
    /** Set camera background color. */
    setBackgroundColor(color: number): void;
    /** Hide/show the skybox layer. */
    setSkyboxVisible(visible: boolean): void;
    /** Tint all active tile sprites black (called each frame during redsky/blacksky). */
    tintAllTilesBlack(): void;
    /** Set tint on the local player sprite. */
    setPlayerTint(color: number): void;
    /** Clear tint on the local player sprite. */
    clearPlayerTint(): void;
    /** Show/hide the local player sprite. */
    setPlayerVisible(visible: boolean): void;
    /** Hide the held block sprite. */
    hideHeldBlock(): void;
    /** Hide the sword via CombatHUD. */
    hideSword(): void;
    /** Called when animation finishes — GameScene launches DeathScreenScene. */
    onAnimationFinished(): void;
}

export class DeathAnimationSystem {
    private scene: Phaser.Scene;
    private processedTextures: ProcessedTextures;
    private callbacks: DeathAnimationCallbacks;

    // Death animation state machine
    private phase: 'freeze' | 'flash' | 'redsky' | 'blacksky' | null = null;
    private phaseStart: number = 0;
    private playerScreenX: number = 0;
    private playerScreenY: number = 0;
    private skull: Phaser.GameObjects.Image | null = null;
    private skullScreenY: number = 0;
    private groundScreenY: number = 0;
    private skullLandedAt: number = 0;

    constructor(
        scene: Phaser.Scene,
        processedTextures: ProcessedTextures,
        callbacks: DeathAnimationCallbacks,
    ) {
        this.scene = scene;
        this.processedTextures = processedTextures;
        this.callbacks = callbacks;
    }

    /** True while any death animation phase is active. */
    get isActive(): boolean {
        return this.phase !== null;
    }

    /**
     * Start the 4-phase death animation.
     * @param playerScreenX  Frozen screen X of the player sprite
     * @param playerScreenY  Frozen screen Y of the player sprite
     * @param groundScreenY  Screen Y where the skull should land
     */
    start(playerScreenX: number, playerScreenY: number, groundScreenY: number): void {
        this.phase = 'freeze';
        this.phaseStart = Date.now();
        this.playerScreenX = playerScreenX;
        this.playerScreenY = playerScreenY;
        this.groundScreenY = groundScreenY;
        this.skullLandedAt = 0;
    }

    /**
     * Advance the death animation by one frame. Call every frame while isActive.
     * Handles phase transitions and visual effects internally.
     */
    update(now: number): void {
        const elapsed = now - this.phaseStart;

        switch (this.phase) {
            case 'freeze':
                // Brief freeze — nothing moves, player visible as-is
                if (elapsed >= DEATH_FREEZE_MS) {
                    this.phase = 'flash';
                    this.phaseStart = now;
                }
                break;

            case 'flash':
                // Flash player sprite solid red 3 times (80ms on, 80ms off)
                if (elapsed >= DEATH_FLASH_MS) {
                    this.phase = 'redsky';
                    this.phaseStart = now;
                    this.callbacks.clearPlayerTint();
                } else {
                    // 3 cycles of 160ms each: 0-80 red, 80-160 clear, 160-240 red, etc.
                    const cycle = Math.floor(elapsed / 80);
                    if (cycle % 2 === 0) {
                        this.callbacks.setPlayerTint(0xff0000);
                    } else {
                        this.callbacks.clearPlayerTint();
                    }
                }
                break;

            case 'redsky': {
                // Sky turns red, all visible tile sprites turn black
                this.callbacks.setBackgroundColor(0x880000);
                // Hide skybox during death
                this.callbacks.setSkyboxVisible(false);
                // Tint all visible tiles black
                this.callbacks.tintAllTilesBlack();
                // Keep player visible with red tint during red sky
                this.callbacks.setPlayerTint(0xff0000);
                if (elapsed >= DEATH_REDSKY_MS) {
                    this.phase = 'blacksky';
                    this.phaseStart = now;
                    // Hide player, show skull at player position
                    this.callbacks.setPlayerVisible(false);
                    this.callbacks.clearPlayerTint();
                    // Hide held block and sword
                    this.callbacks.hideHeldBlock();
                    this.callbacks.hideSword();

                    // Create skull at player's screen position
                    this.skull = atlasCreateImage(
                        this.scene, this.playerScreenX, this.playerScreenY,
                        this.processedTextures.skull
                    );
                    this.skull.setOrigin(0, 0);
                    this.skull.setDepth(100);
                    this.skullScreenY = this.playerScreenY;
                }
                break;
            }

            case 'blacksky': {
                // Sky turns black, skull falls
                this.callbacks.setBackgroundColor(0x000000);
                // Keep tiles black
                this.callbacks.tintAllTilesBlack();
                // Skull falls 2px per frame and stops on ground
                if (this.skull && this.skullLandedAt === 0) {
                    this.skullScreenY += 2;
                    if (this.skullScreenY >= this.groundScreenY) {
                        this.skullScreenY = this.groundScreenY;
                        this.skullLandedAt = now;
                    }
                    this.skull.setY(this.skullScreenY);
                }
                // Transition to death screen after skull rests on ground
                if (this.skullLandedAt > 0 && now - this.skullLandedAt > 800) {
                    this.finish();
                }
                break;
            }
        }
    }

    /** Clean up the animation and signal GameScene to show the death screen. */
    private finish(): void {
        // Clean up skull sprite
        if (this.skull) {
            this.skull.destroy();
            this.skull = null;
        }
        // Keep black background and tinted tiles — restored in handleDeathDismiss()
        // to avoid a 1-frame flash of bright colors before the death screen covers them.
        this.phase = null;
        this.callbacks.onAnimationFinished();
    }

    /** Clean up any active skull sprite (e.g. on scene shutdown). */
    destroy(): void {
        if (this.skull) {
            this.skull.destroy();
            this.skull = null;
        }
        this.phase = null;
    }
}
